import { assertCompleteRows } from "./aggregateState";
// ==========================================================================
// lib/property.ts — investment property (FF-036, 9 Oct 2026) + the shared
// "which vehicle / which property" link used when coding money.
// A property is an asset (kind 'property'); money links to it through
// transaction_splits.asset_id, exactly like fuel links to the Prado.
// ==========================================================================
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "./supabase";
import { useHouseholdId } from "./household";
import type { Category } from "./money";

export type AssetKind = "vehicle" | "property" | "item";
export type AssetOption = { id: string; name: string; kind: AssetKind; disposed_on: string | null };

/** Which kind of asset a budget code's money belongs to, if any.
 *  400s (transport) → a vehicle. 800s (investment property) and 140 Rent → a property. */
export function assetKindFor(categoryId: string | null | undefined, cats: Category[]): "vehicle" | "property" | null {
  if (!categoryId) return null;
  const c = cats.find((x) => x.id === categoryId); if (!c) return null;
  const g = c.is_group ? c : cats.find((x) => x.id === c.parent_id);
  if (g?.code === "400") return "vehicle";
  if (g?.code === "800" || c.code === "140") return "property";
  return null;
}

export function useAssetOptions() {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["assets", hid], enabled: !!hid,
    queryFn: async () => {
      const { data, error } = await supabase.from("assets").select("id,name,kind,disposed_on").eq("household_id", hid).is("deleted_at", null).order("name");
      if (error) throw error;
      return data as AssetOption[];
    },
  });
}

// ---------------------------------------------------------------------------
export type Property = {
  asset_id: string; address: string | null; property_type: "house" | "unit" | "townhouse" | "land" | "other"; bedrooms: number | null;
  value_cents: number | null; value_as_at: string | null; loan_account_id: string | null; loan_deductible_pct: number;
  weekly_rent_cents: number | null; agent_contact_id: string | null; notes: string | null;
  assets: { name: string; acquired_on: string | null; cost_cents: number | null; disposed_on: string | null };
};
export type Ownership = { id: string; asset_id: string; owner_entity_id: string; share_pct: number; valid_from: string | null; valid_to: string | null };
export type UsePeriod = { id: string; asset_id: string; use: "personal" | "work" | "rental" | "vacant"; valid_from: string; valid_to: string | null };

export function useProperties() {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["properties", hid], enabled: !!hid,
    queryFn: async () => {
      const [p, o, u] = await Promise.all([
        supabase.from("properties").select("asset_id,address,property_type,bedrooms,value_cents,value_as_at,loan_account_id,loan_deductible_pct,weekly_rent_cents,agent_contact_id,notes,assets!inner(name,acquired_on,cost_cents,disposed_on,deleted_at)", { count: "exact" }).eq("household_id", hid).is("assets.deleted_at", null),
        supabase.from("asset_ownerships").select("id,asset_id,owner_entity_id,share_pct,valid_from,valid_to", { count: "exact" }).eq("household_id", hid).is("deleted_at", null),
        supabase.from("asset_use_periods").select("id,asset_id,use,valid_from,valid_to", { count: "exact" }).eq("household_id", hid).is("deleted_at", null).order("valid_from"),
      ]);
      if (p.error) throw p.error; if (o.error) throw o.error; if (u.error) throw u.error;
      for (const r of [p, o, u]) assertCompleteRows(r, "Property and ownership");
      const props = (p.data as unknown as Property[]).map((x) => ({ ...x, value_cents: x.value_cents === null ? null : Number(x.value_cents), weekly_rent_cents: x.weekly_rent_cents === null ? null : Number(x.weekly_rent_cents), loan_deductible_pct: Number(x.loan_deductible_pct), assets: { ...x.assets, cost_cents: x.assets.cost_cents === null ? null : Number(x.assets.cost_cents) } }));
      return { props, owners: (o.data as Ownership[]).map((x) => ({ ...x, share_pct: Number(x.share_pct) })), uses: u.data as UsePeriod[] };
    },
  });
}

export type PropertyDraft = {
  asset_id?: string; name: string; address: string; property_type: Property["property_type"]; bedrooms: number | null;
  acquired_on: string | null; cost_cents: number | null; value_cents: number | null; value_as_at: string | null;
  loan_account_id: string | null; loan_deductible_pct: number; weekly_rent_cents: number | null; agent_contact_id: string | null; notes: string | null;
  owners: { entity_id: string; share_pct: number }[];
  use: "rental" | "vacant" | "personal"; use_from: string;
};

/** Save a property: the asset, its details, its owners (replaced when they change) and what it's used for now. */
export function useSaveProperty() {
  const hid = useHouseholdId(); const qc = useQueryClient();
  return useMutation({
    mutationFn: async (d: PropertyDraft) => {
      let id = d.asset_id;
      if (!id) {
        const { data, error } = await supabase.from("assets").insert({ household_id: hid, kind: "property", name: d.name, acquired_on: d.acquired_on, cost_cents: d.cost_cents }).select("id").single();
        if (error) throw error;
        id = (data as { id: string }).id;
      } else {
        const { error } = await supabase.from("assets").update({ name: d.name, acquired_on: d.acquired_on, cost_cents: d.cost_cents }).eq("id", id);
        if (error) throw error;
      }
      const row = { asset_id: id, household_id: hid, address: d.address || null, property_type: d.property_type, bedrooms: d.bedrooms, value_cents: d.value_cents, value_as_at: d.value_as_at,
        loan_account_id: d.loan_account_id, loan_deductible_pct: d.loan_deductible_pct, weekly_rent_cents: d.weekly_rent_cents, agent_contact_id: d.agent_contact_id, notes: d.notes };
      const { error: pe } = await supabase.from("properties").upsert(row, { onConflict: "asset_id" });
      if (pe) throw pe;

      // Owners: if they changed, retire the current rows and add the new ones.
      const { data: cur } = await supabase.from("asset_ownerships").select("id,owner_entity_id,share_pct").eq("asset_id", id).is("deleted_at", null);
      const now = (cur ?? []) as { id: string; owner_entity_id: string; share_pct: number }[];
      const same = now.length === d.owners.length && d.owners.every((o) => now.some((n) => n.owner_entity_id === o.entity_id && Number(n.share_pct) === o.share_pct));
      if (!same) {
        for (const n of now) { const { error } = await supabase.from("asset_ownerships").update({ deleted_at: new Date().toISOString() }).eq("id", n.id); if (error) throw error; }
        for (const o of d.owners) { const { error } = await supabase.from("asset_ownerships").insert({ household_id: hid, asset_id: id, owner_entity_id: o.entity_id, share_pct: o.share_pct, valid_from: d.acquired_on }); if (error) throw error; }
      }

      // Use: one open period. A different use closes the open one the day before and starts the new one.
      const { data: open } = await supabase.from("asset_use_periods").select("id,use,valid_from").eq("asset_id", id).is("valid_to", null).is("deleted_at", null).maybeSingle();
      const o = open as { id: string; use: string; valid_from: string } | null;
      if (!o) {
        const { error } = await supabase.from("asset_use_periods").insert({ household_id: hid, asset_id: id, use: d.use, valid_from: d.use_from });
        if (error) throw error;
      } else if (o.use === d.use && o.valid_from !== d.use_from) {
        // Same use, corrected start date (e.g. the lease started earlier than the day it was added).
        const { error } = await supabase.from("asset_use_periods").update({ valid_from: d.use_from }).eq("id", o.id);
        if (error) throw error;
      } else if (o.use !== d.use) {
        const dayBefore = new Date(new Date(d.use_from).getTime() - 86400000).toISOString().slice(0, 10);
        const { error: e1 } = await supabase.from("asset_use_periods").update({ valid_to: dayBefore < o.valid_from ? o.valid_from : dayBefore }).eq("id", o.id); if (e1) throw e1;
        const { error: e2 } = await supabase.from("asset_use_periods").insert({ household_id: hid, asset_id: id, use: d.use, valid_from: d.use_from }); if (e2) throw e2;
      }
      // The agent's lines belong to this property unless the contact already points elsewhere.
      if (d.agent_contact_id) {
        await supabase.from("contacts").update({ default_asset_id: id }).eq("id", d.agent_contact_id).is("default_asset_id", null);
      }
      return id;
    },
    onSuccess: () => { for (const k of ["properties", "assets", "audit", "contacts"]) void qc.invalidateQueries({ queryKey: [k, hid] }); },
  });
}

/** Coded money for one property, per month per code (spending negative, rent positive). */
export function usePropertyTotals(assetId: string | undefined, from: string, to: string) {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["split_totals", hid, "property", assetId, from, to], enabled: !!hid && !!assetId,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("property_month_totals", { p_household: hid, p_asset: assetId, p_from: from, p_to: to });
      if (error) throw error;
      return (data as { month: string; category_id: string; amount_cents: number; lines: number }[]).map((r) => ({ ...r, amount_cents: Number(r.amount_cents), lines: Number(r.lines) }));
    },
  });
}

export type PropertyLine = { id: string; amount_cents: number; category_id: string; transactions: { id: string; posted_on: string; description: string; account_id: string; contact_id: string | null } };
/** Every coded line for one property in a period, newest first (paged past 1,000). */
export function usePropertyLines(assetId: string | undefined, from: string, to: string) {
  const hid = useHouseholdId();
  return useQuery({
    queryKey: ["split_totals", hid, "property_lines", assetId, from, to], enabled: !!hid && !!assetId,
    queryFn: async () => {
      const out: PropertyLine[] = [];
      for (let at = 0; ; at += 1000) {
        const { data, error } = await supabase.from("transaction_splits")
          .select("id,amount_cents,category_id,transactions!inner(id,posted_on,description,account_id,contact_id,deleted_at)")
          .eq("household_id", hid).eq("asset_id", assetId!).gte("transactions.posted_on", from).lte("transactions.posted_on", to).is("transactions.deleted_at", null)
          .range(at, at + 999);
        if (error) throw error;
        out.push(...(data as unknown as PropertyLine[]).map((r) => ({ ...r, amount_cents: Number(r.amount_cents) })));
        if ((data ?? []).length < 1000) break;
      }
      return out.sort((a, b) => b.transactions.posted_on.localeCompare(a.transactions.posted_on));
    },
  });
}

/** Rental-type lines (140 rent, 800s) that aren't linked to any property yet. */
export function useUnlinkedRental(cats: Category[]) {
  const hid = useHouseholdId();
  const ids = cats.filter((c) => assetKindFor(c.id, cats) === "property").map((c) => c.id);
  return useQuery({
    queryKey: ["split_totals", hid, "unlinked_rental", ids.join(",")], enabled: !!hid && ids.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase.from("transaction_splits").select("id,amount_cents").eq("household_id", hid).in("category_id", ids).is("asset_id", null).limit(1000);
      if (error) throw error;
      return data as { id: string; amount_cents: number }[];
    },
  });
}

export function useLinkSplits() {
  const hid = useHouseholdId(); const qc = useQueryClient();
  return useMutation({
    mutationFn: async (a: { splitIds: string[]; assetId: string }) => {
      for (let i = 0; i < a.splitIds.length; i += 200) {
        const { error } = await supabase.from("transaction_splits").update({ asset_id: a.assetId }).in("id", a.splitIds.slice(i, i + 200));
        if (error) throw error;
      }
    },
    onSuccess: () => { for (const k of ["split_totals", "txns", "audit"]) void qc.invalidateQueries({ queryKey: [k, hid] }); },
  });
}
